import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Copy, Crown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { get, post, verifyLicense } from "../lib/pro";
import { money } from "./PayInvoice";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-muted/40 flex items-center justify-center p-5" data-testid="payment-page">
    <div className="lw-card w-full max-w-md p-7 text-center space-y-4">{children}</div>
  </div>
);

export function PaymentCancel() {
  return (
    <Shell>
      <XCircle className="h-12 w-12 mx-auto text-muted-foreground" strokeWidth={1.5} />
      <h1 className="text-2xl font-semibold" data-testid="payment-cancel-title">Payment cancelled</h1>
      <p className="text-sm text-muted-foreground">No money was taken. You can try again any time.</p>
      <Button asChild data-testid="payment-back-btn"><Link to="/">Back to documents</Link></Button>
    </Shell>
  );
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const ppToken = params.get("token");
  const sid = params.get("session_id") || (ppToken ? `paypal:${ppToken}` : null);
  const [st, setSt] = useState({ state: "checking" });

  useEffect(() => {
    if (!sid) return setSt({ state: "error" });
    let n = 0, stop = false;
    const poll = async () => {
      try {
        const r = ppToken && n === 0 ? await post("/api/paypal/capture", { order_id: ppToken }) : await get(`/api/payments/status/${sid}`);
        if (r.payment_status === "paid") {
          if (stop) return;
          if (r.license_key) await verifyLicense(r.license_key).catch(() => {});
          return setSt({ state: "paid", ...r });
        }
        if (r.status === "expired" || r.payment_status === "failed") return setSt({ state: "error" });
      } catch (x) { if (x.status === 404 || (ppToken && n === 0)) return setSt({ state: "error" }); }
      if (!stop && ++n < 20) setTimeout(poll, 2000);
      else if (!stop) setSt({ state: "pending" });
    };
    poll();
    return () => { stop = true; };
  }, [sid, ppToken]);

  if (st.state === "checking") return <Shell><Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" /><p className="text-sm" data-testid="payment-checking">Confirming your payment…</p></Shell>;
  if (st.state !== "paid") return (
    <Shell>
      <XCircle className="h-12 w-12 mx-auto text-amber-500" strokeWidth={1.5} />
      <h1 className="text-xl font-semibold" data-testid="payment-pending-title">{st.state === "pending" ? "Payment still processing" : "Payment not completed"}</h1>
      <p className="text-sm text-muted-foreground">{st.state === "pending" ? "PayNow transfers can take a minute. Refresh this page shortly." : "The payment did not go through. No money was taken."}</p>
      <Button asChild variant="outline"><Link to="/">Back to documents</Link></Button>
    </Shell>
  );
  const amount = `${(st.currency || "sgd").toUpperCase()} ${money(st.amount)}`;
  return (
    <Shell>
      <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" strokeWidth={1.5} />
      <h1 className="text-2xl font-semibold" data-testid="payment-success-title">{st.kind === "pro" ? "Welcome to PaperLite Pro" : "Payment received"}</h1>
      <p className="text-sm text-muted-foreground" data-testid="payment-amount">{amount} paid. Thank you!</p>
      {st.license_key && (
        <div className="rounded-lg border bg-muted/50 p-3 text-left">
          <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Crown className="h-3.5 w-3.5 text-amber-500" />Your licence key — keep it to activate Pro on other devices</div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <span className="font-mono text-sm" data-testid="payment-license-key">{st.license_key}</span>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(st.license_key); toast("Copied"); }} data-testid="payment-copy-license"><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      )}
      {st.kind === "pro" ? <Button asChild data-testid="payment-back-btn"><Link to="/">Start writing</Link></Button>
        : <p className="text-xs text-muted-foreground">You can close this page.</p>}
    </Shell>
  );
}
